import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import {
  account,
  getCurrentUser,
  signOut,
  selectTeam,
  completeEmailSignIn,
  connectOAuthProvider,
  sendEmailSignInLink
} from '../services/appwrite';
import {
  connectFailureMessage,
  finishIdentify,
  getOAuthProvider,
  isExistingAccountRefusal,
  isIdentifyResponse,
  parseRedirectError,
  providerLabel,
  setFlash,
  setLoginNotice,
  setPendingLink,
  startIdentify,
  takePendingLink,
  takeReturnTo
} from '../lib/signInMethods';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

/**
 * Every sign-in comes back through here: OAuth success and failure, emailed
 * sign-in links, connecting a provider, and a refused provider telling us who
 * the user is. It works out what happened and moves on — to the app, or to a
 * plain /login with a notice in storage (never in the URL; see
 * setLoginNotice), so nothing about the attempt is left in the address bar.
 */
const AuthCallback = () => {
  const [processing, setProcessing] = useState(true);
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [teams, setTeams] = useState([]);
  const [authInfo, setAuthInfo] = useState(null);

  // Off to /login (the render below), with what to tell them there.
  const leaveForLogin = (notice) => {
    setLoginNotice(notice);
    setProcessing(false);
  };

  useEffect(() => {
    const processCallback = async () => {
      try {
        console.log('AuthCallback: Processing OAuth callback');

        // Check for OAuth errors in URL
        const urlParams = new URLSearchParams(window.location.search);
        const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const error_code = urlParams.get('error');
        const error_description = urlParams.get('error_description');

        // A refused provider answering "who is this?" (see below). Email a
        // sign-in link to the address it gave; once that link is used, the
        // queued provider is connected.
        if (isIdentifyResponse(fragment)) {
          const identified = finishIdentify(fragment);
          if (!identified.provider) {
            console.warn('Provider answer did not match a request from this browser');
            takePendingLink();
            return leaveForLogin({ type: 'error', code: 'sign_in_failed' });
          }
          if (!identified.email) {
            console.warn('Provider did not say who this is:', identified.error);
            return leaveForLogin({ type: 'link-email', provider: identified.provider });
          }
          try {
            await sendEmailSignInLink(identified.email);
            return leaveForLogin({ type: 'link-sent', provider: identified.provider, email: identified.email });
          } catch (sendError) {
            console.error('Could not send sign-in link:', sendError);
            return leaveForLogin({ type: 'link-email', provider: identified.provider, email: identified.email });
          }
        }

        // A provider sign-in Appwrite turned away. When it is because the
        // email already has an account the provider isn't connected to, ask
        // the provider for that email and send them a sign-in link instead.
        const failedProvider = urlParams.get('failed');
        if (failedProvider) {
          const failure = parseRedirectError(error_code);
          if (getOAuthProvider(failedProvider) && isExistingAccountRefusal(failure)) {
            setPendingLink(failedProvider);
            const identifyUrl = startIdentify(failedProvider, `${window.location.origin}/auth/callback`);
            if (identifyUrl) {
              window.location.replace(identifyUrl);
              return;
            }
            return leaveForLogin({ type: 'link-email', provider: failedProvider });
          }
          console.error('Sign-in failed:', failedProvider, failure);
          return leaveForLogin({ type: 'error', code: 'sign_in_failed' });
        }

        // Back from connecting a provider to an account that was already
        // signed in. A refusal leaves that session in place, so either way the
        // result is reported and sign-in carries on as normal.
        const connected = urlParams.get('connected');
        const connectFailed = urlParams.get('connect_failed');
        if (connectFailed) {
          console.warn('Connecting a sign-in provider was refused:', connectFailed, error_code);
          setFlash('error', connectFailureMessage(connectFailed, parseRedirectError(error_code)));
        } else if (connected) {
          setFlash('success', `${providerLabel(connected)} is connected. You can use it to sign in from now on.`);
        } else if (error_code) {
          console.error('OAuth error:', error_code, error_description);
          return leaveForLogin({ type: 'error', code: 'sign_in_failed' });
        }

        // An emailed sign-in link: exchange its secret for a session.
        const linkUserId = urlParams.get('userId');
        const linkSecret = urlParams.get('secret');
        if (linkUserId && linkSecret) {
          try {
            await completeEmailSignIn(linkUserId, linkSecret);
          } catch (linkError) {
            // Already signed in in this browser: the existing session will do.
            if (linkError?.type !== 'user_session_already_exists') {
              console.error('Email sign-in link rejected:', linkError);
              return leaveForLogin({ type: 'error', code: 'email_link_invalid' });
            }
          }
        }

        // IMPORTANT: If we reach this callback page, Appwrite has ALREADY handled OAuth
        // and should have set the session cookie. We just need to verify it exists.
        // If there's no session, it means OAuth failed at Appwrite's end.
        console.log('Checking for Appwrite session...');
        let session;
        try {
          session = await account.getSession('current');
          console.log('Session verified, provider:', session.provider);
        } catch (sessionError) {
          console.error('No session found - OAuth may have failed:', sessionError);
          console.error('This usually means:');
          console.error('1. OAuth provider not configured correctly in Appwrite Console');
          console.error('2. User cancelled OAuth or provider rejected');
          console.error('3. Redirect URL mismatch in provider settings');
          return leaveForLogin({ type: 'error', code: 'authentication_failed' });
        }

        // Get current user via secure auth-resolve function
        const currentUser = await getCurrentUser();

        if (!currentUser) {
          // getCurrentUser() couldn't resolve the account at all — that's a
          // genuine failure, not a new signup (which returns a user object
          // flagged `requires_team_setup`, handled just below).
          console.error('Could not resolve the authenticated user');
          await signOut();
          return leaveForLogin({ type: 'error', code: 'no_user_record' });
        }

        // A provider queued to connect once they were in — the one that was
        // refused because this email already had an account.
        // Connecting comes back here with `connected` or `connect_failed`.
        const pendingLink = takePendingLink();
        if (pendingLink) {
          console.log('Connecting queued sign-in provider:', pendingLink);
          connectOAuthProvider(pendingLink);
          return;
        }

        // A session with no organisation behind it is a signup in progress, not
        // a failure. Send them to step two rather than logging them out — this
        // is the normal path for everyone who signs up from the website.
        if (currentUser.requires_team_setup) {
          console.log('User has no organisation — continuing signup');
          window.location.href = '/register';
          return;
        }

        // Check if user needs to select a team (multi-team user)
        if (currentUser.requires_team_selection) {
          console.log('User has multiple teams, showing selector');
          setTeams(currentUser.teams);
          setAuthInfo({
            id: currentUser.id,
            email: currentUser.email,
            name: currentUser.name
          });
          setShowTeamSelector(true);
          setProcessing(false);
          return;
        }

        console.log('User authenticated successfully:', currentUser.email);
        console.log('Redirecting to dashboard...');
        
        // Use window.location to force a full page reload so AuthContext can initialize
        window.location.href = takeReturnTo() || '/dashboard';
      } catch (err) {
        console.error('OAuth callback error:', err);
        leaveForLogin({ type: 'error', code: 'authentication_failed' });
      }
    };

    processCallback();
  }, []);

  // Handle team selection
  const handleSelectTeam = async (team) => {
    try {
      setProcessing(true);
      
      // Store the selected team
      selectTeam(team.id);
      
      console.log('Team selected:', team.name);
      console.log('Redirecting to dashboard...');
      
      // Use window.location to force a full page reload
      window.location.href = takeReturnTo() || '/dashboard';
    } catch (err) {
      console.error('Error selecting team:', err);
      leaveForLogin({ type: 'error', code: 'authentication_failed' });
    }
  };

  // Show team selector
  if (showTeamSelector && teams.length > 0) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-background">
        <ThemeToggle />
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Select Organisation</CardTitle>
            <CardDescription>
              Welcome back, {authInfo?.name || authInfo?.email}! 
              You have access to multiple organisations.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {teams.map((team) => (
              <Button
                key={team.id}
                variant="outline"
                className="w-full justify-start h-auto py-4 px-4"
                onClick={() => handleSelectTeam(team)}
              >
                <div className="text-left">
                  <div className="font-semibold">{team.name}</div>
                </div>
              </Button>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading while processing
  if (processing) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-background">
        <ThemeToggle />
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Skeleton className="h-12 w-12 rounded-full" />
            </div>
            <CardTitle className="text-2xl">Signing you in...</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <div className="flex justify-center space-x-2">
              <Skeleton className="h-4 w-4 rounded-full animate-bounce" />
              <Skeleton className="h-4 w-4 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
              <Skeleton className="h-4 w-4 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Done here: /login, with any notice already in storage. Replacing this
  // entry also drops the callback URL (and anything in it) from history.
  return <Navigate to="/login" replace />;
};

export default AuthCallback;